import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import {
  deleteGolfCourse,
  createGolfCourse,
  updateGolfCourse,
  getAllGolfCoursesPaginated,
} from '@/api/golfCourse/golfCourse.actions';
import {
  GolfCourse,
  CreateGolfCourseData,
} from '@/api/golfCourse/golfCourse.types';
import { isApiSuccess } from '@/api/shared.types.ts';
import { useAsync } from '@/hooks/global/useAsync';

const DEFAULT_PAGE_SIZE = 10;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useGolfCourses = (pageSize = DEFAULT_PAGE_SIZE) => {
  const [golfCoursesCount, setGolfCoursesCount] = useState(0);
  const [golfCourses, setGolfCourses] = useState<GolfCourse[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCourse, setSelectedCourse] = useState<GolfCourse | null>(null);
  const { run, isLoading } = useAsync();
  const { run: runFetchAll, isLoading: isFetchingAll } = useAsync();

  const fetchCourses = async (page: number): Promise<void> => {
    const response = await runFetchAll(() =>
      getAllGolfCoursesPaginated(page, pageSize)
    );
    if (!isApiSuccess(response)) {
      toast.error(response.error.message);
      return;
    }
    setGolfCourses(response.content.data);
    setGolfCoursesCount(response.content.pagination.total);
  };

  const createCourse = async (data: CreateGolfCourseData): Promise<void> => {
    const response = await run(() => createGolfCourse(data));
    if (!isApiSuccess(response)) {
      toast.error(response.error.message);
      return;
    }
    setCurrentPage(1);
    toast.success('Golf course created successfully.');
  };

  const updateCourse = async (
    id: string,
    data: CreateGolfCourseData
  ): Promise<void> => {
    const response = await run(() => updateGolfCourse(id, data));
    if (!isApiSuccess(response)) {
      toast.error(response.error.message);
      return;
    }
    await fetchCourses(currentPage);
    toast.success('Golf course updated successfully.');
  };

  const deleteCourse = async (course: GolfCourse): Promise<void> => {
    const response = await run(() => deleteGolfCourse(course.id));
    if (!isApiSuccess(response)) {
      toast.error(response.error.message);
      return;
    }
    await fetchCourses(currentPage);
    toast.success('Golf course deleted.');
  };

  useEffect(() => {
    fetchCourses(currentPage);
  }, [currentPage]);

  return {
    golfCoursesCount,
    golfCourses,
    currentPage,
    setCurrentPage,
    pageSize,
    selectedCourse,
    setSelectedCourse,
    createCourse,
    updateCourse,
    deleteCourse,
    isLoading,
    isFetchingAll,
  };
};
